import { Modal, Spin, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ReactDOM from 'react-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// Helper functions
const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

const formatTime = (dateTime) => {
    if (!dateTime) return '';
    const d = new Date(dateTime);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

// Envelope template
const enveloppeTemplate = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>DEVIS - {{REF_DEV}}</title>
    <style>
        @media print {
            .page { 
                page-break-after: always;
                box-sizing: border-box;
            }
        }
        @page { size: A4; margin: 1cm; }
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 5px;
            font-size: 12px;
            line-height: 1.2;
            text-align: center;
        }
        .envelope-container {
            padding: 15px 30px;
            margin-bottom: 20px;
            margin-left: auto;
            margin-right: auto;
            max-width: 80%;
        }
        .devis-number {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 15px;
            text-align: center;
        }
        .info-row {
            display: flex;
            justify-content: center;
            margin-bottom: 15px;
        }
        .date-time {
            font-size: 14px;
            margin-right: 30px;
            text-align: center;
        }
        .commercial {
            font-size: 14px;
            text-align: center;
        }
        .client-name {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 10px;
            margin-top: 20px;
            text-align: center;
        }
        .client-info {
            font-size: 14px;
            text-align: center;
        }
        .field-label {
            font-weight: bold;
            margin-right: 5px;
        }
        .field-row {
            margin-bottom: 8px;
            text-align: center;
        }
    </style>
</head>
<body>
    <div class="page">
        <div class="envelope-container">
            <div class="devis-number">DEVIS N°: {{REF_DEV}}</div>
            
            <div class="info-row">
                <div class="date-time">
                    <div class="field-row">
                        <span class="field-label">Date Validation:</span>
                        <span>{{DATE_VALIDDEVIS}} à {{HEURE_VALID}}</span>
                    </div>
                </div>
                <div class="commercial">
                    <div class="field-row">
                        <span class="field-label">Commercial:</span>
                        <span>{{COMMERCIAL}}</span>
                    </div>
                </div>
            </div>
            
            <div class="client-name">{{CLIENT}}</div>
            
            <div class="client-info">
                <div class="field-row">
                    <span class="field-label">Adresse:</span>
                    <span>{{ADRESSE_CLIENT}}</span>
                </div>
                <div class="field-row">
                    <span class="field-label">Ville:</span>
                    <span>{{VILLE_CLIENT}}</span>
                </div>
                <div class="field-row">
                    <span class="field-label">Téléphone:</span>
                    <span>{{TEL_CLIENT}}</span>
                </div>
                <div class="field-row">
                    <span class="field-label">Mode Livraison:</span>
                    <span>{{MODE_LIV}}</span>
                </div>
            </div>
        </div>
    </div>
</body>
</html>`;

const processTemplate = (template, replacements) => {
    let processedTemplate = template;
    Object.entries(replacements).forEach(([key, value]) => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        processedTemplate = processedTemplate.replace(regex, value || '');
    });
    return processedTemplate;
};

// PDF generation helper function
const generatePdfBlob = async (htmlString) => {
    const container = document.createElement('div');
    container.style.width = '210mm';
    container.style.position = 'absolute';
    container.style.left = '-10000px';
    container.innerHTML = htmlString;
    document.body.appendChild(container);
    
    try {
        const pages = container.querySelectorAll('.page');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const margin = 10; // 10mm for sides/bottom
    
        for (let i = 0; i < pages.length; i++) {
            const page = pages[i];
            
            // Set specific styles for capturing
            page.style.width = '210mm';
            page.style.height = '297mm';
            page.style.padding = `10mm ${margin}mm ${margin}mm ${margin}mm`;
            page.style.boxSizing = 'border-box';
            page.style.backgroundColor = '#FFFFFF';
    
            const canvas = await html2canvas(page, {
                scale: 3,
                useCORS: true,
                logging: false,
                letterRendering: true,
                backgroundColor: '#FFFFFF',
                width: page.offsetWidth,
                height: page.offsetHeight,
                fontFamily: 'Arial',
            });
            // Add image to PDF
            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            if (i > 0) pdf.addPage();
            pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297); // Direct A4 dimensions
        }
    
        return pdf.output('blob');
    } finally {
        document.body.removeChild(container);
    }
};

// Helper function to get the appropriate data
const getTemplateData = (data, commercial) => {
    // Use devis data if available, otherwise fallback to facture data
    const devisData = data.devisData || {};
   
    console.log('Devis data for envelope:', devisData);
    
    // Format validation date and time
    let formattedDate = '';
    let formattedTime = '';
    
    // Use specific devis validation date if available - prioritize devis data
    if (devisData.DATEVALID) {
        formattedDate = formatDate(devisData.DATEVALID);
        // If HEUREVALID is a string, use it directly, otherwise format it
        formattedTime = devisData.HEUREVALID && typeof devisData.HEUREVALID === 'string' 
            ? devisData.HEUREVALID 
            : formatTime(devisData.DATEVALID);
        
        console.log(`Using devis date: ${formattedDate}, time: ${formattedTime}`);
    } else if (data.DATEVALID_DEVIS) {
        formattedDate = formatDate(data.DATEVALID_DEVIS);
        formattedTime = formatTime(data.DATEVALID_DEVIS);
        console.log(`Using passed devis date: ${formattedDate}, time: ${formattedTime}`);
    } else if (data.DATEVALID) {
        // Fallback to facture validation date
        formattedDate = formatDate(data.DATEVALID);
        formattedTime = formatTime(data.DATEVALID);
        console.log(`Using facture date: ${formattedDate}, time: ${formattedTime}`);
    }
    
    // Get commercial info from devis if available
    const commercialName = commercial 
        ? `${commercial.nom} (${commercial.code_com})`
        : devisData.comercial 
            ? `${devisData.comercial.nom} (${devisData.comercial.code_com})`
            : '';
    
    return {
        REF_DEV: data.REF_DEV || devisData.REF_DEV || '',
        DATE_VALIDDEVIS: formattedDate,
        HEURE_VALID: formattedTime,
        COMMERCIAL: commercialName,
        MODE_LIV: data.MODELIV || devisData.MODELIV || '',
        CLIENT: data.CLIENT || devisData.CLIENT || '',
        ADRESSE_CLIENT: data.client?.ADRESSE_SIEGE || devisData.client?.ADRESSE_SIEGE || '',
        VILLE_CLIENT: data.client?.VILLE || devisData.client?.VILLE || '',
        TEL_CLIENT: data.client?.TEL || data.client?.MOBILE || devisData.client?.TEL || devisData.client?.MOBILE || '',
        CURRENT_DATE: formatDate(new Date())
    };
};

// Main export function for envelope preview
export const printEnveloppe = async (data, commercial = null) => {
    try {
        // Create loading modal with "Sortir" button
        const loadingModalDiv = document.createElement('div');
        document.body.appendChild(loadingModalDiv);
        
        const closeLoadingModal = () => {
            ReactDOM.unmountComponentAtNode(loadingModalDiv);
            document.body.removeChild(loadingModalDiv);
        };
        
        ReactDOM.render(
            <Modal
                title="Generating PDF"
                open={true}
                closable={true}
                maskClosable={false}
                footer={[
                    <Button key="exit" onClick={closeLoadingModal}>
                        Sortir
                    </Button>
                ]}
            >
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <Spin 
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                        tip="Preparing document..."
                    />
                </div>
            </Modal>,
            loadingModalDiv
        );

        // Process the template with data
        const html = processTemplate(enveloppeTemplate, getTemplateData(data, commercial));

        // Generate the PDF blob
        const pdfBlob = await generatePdfBlob(html);
        
        // Close loading modal
        closeLoadingModal();

        // Create URL for the blob
        const pdfUrl = URL.createObjectURL(pdfBlob);
        
        // Create preview modal with "Sortir" button
        const previewModalDiv = document.createElement('div');
        document.body.appendChild(previewModalDiv);
        
        const closePreviewModal = () => {
            URL.revokeObjectURL(pdfUrl);
            ReactDOM.unmountComponentAtNode(previewModalDiv);
            document.body.removeChild(previewModalDiv);
        };
        
        ReactDOM.render(
            <Modal
                title={`PDF Preview - Enveloppe ${data.REF_DEV || ''}`}
                open={true}
                width="90%"
                centered={true}
                closable={true}
                maskClosable={true}
                className="pdf-preview-modal"
                style={{ top: 20, maxWidth: '1400px' }}
                footer={[
                    <Button key="exit" onClick={closePreviewModal}>
                        Sortir
                    </Button>
                ]}
                onCancel={closePreviewModal}
            >
                <div className="pdf-preview-container">
                    <iframe
                        src={pdfUrl}
                        style={{ width: '100%', height: '80vh', border: 'none' }}
                        title="PDF Preview"
                    />
                </div>
            </Modal>,
            previewModalDiv
        );

        return {
            cleanup: closePreviewModal,
            pdfUrl
        };

    } catch (error) {
        console.error('Error generating envelope:', error);
        Modal.error({
            title: 'Error',
            content: `Failed to generate document: ${error.message}`,
        });
        throw new Error(`Failed to generate document: ${error.message}`);
    }
};

// Direct print function for envelope
export const printEnveloppeDirect = async (data, commercial = null) => {
    try {
        // Process the template with data
        const html = processTemplate(enveloppeTemplate, getTemplateData(data, commercial));

        // Create an invisible iframe for printing
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position: absolute; width: 0; height: 0; left: -9999px; border: 0;';
        document.body.appendChild(iframe);
        
        // Write the HTML content to the iframe
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(html);
        doc.close();
        
        // Set up cleanup after printing
        iframe.onload = () => {
            setTimeout(() => {
                try {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                    
                    // Remove the iframe after printing
                    setTimeout(() => {
                        document.body.removeChild(iframe);
                    }, 1000);
                } catch (err) {
                    console.error('Error in print process:', err);
                    document.body.removeChild(iframe);
                }
            }, 500);
        };
    } catch (error) {
        console.error('Error generating envelope for direct print:', error);
        throw new Error(`Failed to generate document: ${error.message}`);
    }
}; 